import React from 'react';
import { CheckCircle2, Circle, CircleDot, Loader2, Mail, Plus, UsersRound, X } from 'lucide-react';
import { Button } from '../ui-v2';
import { Input } from '../ui-v2.5';

/**
 * Las piezas que comparten la pantalla del alta y la de la baja (FASE_PANTALLA_DE_ALTA_Y_BAJA.md,
 * §6, piezas 3 y 4). Se estrenan en la baja; el alta las reutiliza, y por eso no saben de cuál de
 * los dos procesos son: todo lo que pintan les llega por props.
 */

/* ── LA BARRA DE ACCIONES ─────────────────────────────────────────────────────────────────────
 * Todas las acciones del proceso, con forma de botón y en el mismo sitio. Ninguna dentro de una
 * línea de datos: es la lección del enlace «Editar» de 11 px que nadie encontraba. */
export function BarraAcciones({ izquierda, derecha }) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-v25-line bg-white/95 py-2.5 backdrop-blur">
      {izquierda}
      <span className="flex-1" />
      {/* `ml-auto` para que, si la barra no cabe en una línea, la acción de cierre siga a la
          derecha y no caiga al principio de la segunda. */}
      <div className="ml-auto">{derecha}</div>
    </div>
  );
}

/* La franja fina: nombre, número y estado, cuando la cabecera ya no se ve. */
export function FranjaFina({ nombre, numero, estado }) {
  return (
    <div className="-mx-4 flex flex-wrap items-center gap-x-3 gap-y-1 bg-v25-brand-800 px-4 py-1.5 text-sm text-white">
      <span className="font-semibold">{nombre}</span>
      <span className="font-mono text-xs text-white/70">{numero}</span>
      {estado}
    </div>
  );
}

/* ── EL TOTAL, JUNTO A LO QUE SUMA ────────────────────────────────────────────────────────────
 * El total y sus partes en la MISMA línea («3 de 9 = Accesos 2/4 + Otras tareas 1/5»), y una barra
 * con un tramo por parte. Cada parte lleva a su columna. Así no hace falta ninguna frase que
 * explique qué cuenta el porcentaje: se ve.
 *
 * `partes`: [{ clave, nombre, hechas, total, color }]. El total es SU suma; quien llama comprueba
 * que coincide con el del servidor (ver `DetalleBaja`), porque si no coincidiera la línea mentiría. */
export function BandaTotal({ verbo, partes }) {
  const total = partes.reduce((s, p) => s + p.total, 0);
  const hechas = partes.reduce((s, p) => s + p.hechas, 0);
  const pct = total ? Math.round((hechas * 100) / total) : 0;
  return (
    <section className="rounded-v25-lg border border-v25-line bg-white p-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
        <h2 className="text-sm font-semibold text-v25-ink-700">{verbo}</h2>
        <span className="text-2xl font-semibold tabular-nums text-v25-ink-900">
          {hechas} <span className="text-base font-normal text-v25-ink-400">de {total}</span>
        </span>
        <span className="text-sm tabular-nums text-v25-ink-500">{pct}%</span>
        {partes.length > 1 && <span className="text-v25-ink-300">=</span>}
        <div className="flex flex-wrap items-center gap-1 text-sm">
          {partes.map((p, i) => (
            <React.Fragment key={p.clave}>
              {i > 0 && <span className="text-v25-ink-300">+</span>}
              <a href={`#col-${p.clave}`} className="flex items-center gap-1.5 rounded-v25-md px-2 py-1 hover:bg-v25-surface-raised">
                <span className="h-2.5 w-2.5 rounded-v25-full" style={{ background: p.color }} />
                <span className="text-v25-ink-500">{p.nombre}</span>
                <span className="font-semibold tabular-nums text-v25-ink-900">{p.hechas}/{p.total}</span>
              </a>
            </React.Fragment>
          ))}
        </div>
      </div>
      {total > 0 && (
        <div className="mt-3 flex h-2.5 overflow-hidden rounded-v25-full bg-v25-line">
          {partes.filter((p) => p.total > 0).map((p, i) => (
            <div key={p.clave} className="relative h-full" style={{ width: `${(p.total / total) * 100}%`, background: `${p.color}26`, marginLeft: i ? 2 : 0 }}>
              <div className="h-full" style={{ width: `${(p.hechas / p.total) * 100}%`, background: p.color }} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* La cabecera de cada columna: su color (el de su tramo en la banda), su nombre y su cuenta. */
export function CabeceraColumna({ clave, color, icono: Icono, titulo, hechas, total, derecha }) {
  return (
    <header id={`col-${clave}`} className="flex scroll-mt-40 items-center gap-2 border-b border-v25-line px-4 py-2.5">
      {color && <span className="h-2.5 w-2.5 shrink-0 rounded-v25-full" style={{ background: color }} />}
      {Icono && <Icono className="h-4 w-4 shrink-0 text-v25-ink-400" />}
      <h2 className="text-sm font-semibold text-v25-ink-900">{titulo}</h2>
      {derecha}
      {total != null && <span className="ml-auto text-sm font-semibold tabular-nums">{hechas}/{total}</span>}
    </header>
  );
}

/* ── EL ESTADO DE UNA TAREA, COMO PÍLDORA ─────────────────────────────────────────────────────
 * Clic para pasar al siguiente: pendiente → en curso → hecha → pendiente. */
const PILDORA = {
  pendiente: { Icono: Circle, texto: 'Pendiente', clases: 'border-v25-info-bd bg-v25-info-bg text-v25-info-fg' },
  en_curso: { Icono: CircleDot, texto: 'En curso', clases: 'border-v25-progress-bd bg-v25-progress-bg text-v25-progress-fg' },
  completada: { Icono: CheckCircle2, texto: 'Hecha', clases: 'border-v25-active-bd bg-v25-active-bg text-v25-active-fg' },
};
export function EstadoTarea({ estado, onClick, disabled, ocupada, etiqueta }) {
  const p = PILDORA[estado] || PILDORA.pendiente;
  const { Icono } = p;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || ocupada}
      aria-label={etiqueta}
      title={disabled ? undefined : 'Clic para cambiar el estado'}
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-v25-full border px-2 py-0.5 text-[11px] font-medium disabled:cursor-not-allowed disabled:opacity-60 ${p.clases}`}
    >
      {ocupada ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icono className="h-3 w-3" />}
      {p.texto}
    </button>
  );
}

/* ── QUIÉN SE ENTERA ──────────────────────────────────────────────────────────────────────────
 *
 * Tres grupos en vez de dos frases. En el drawer, dos notas explicaban que «quitar un área no
 * silencia a quien tenga una tarea» y que «cambiar las áreas no envía nada». Aquí:
 *
 *   · las áreas que se enteran POR LO QUE TIENEN QUE HACER van aparte y no tienen ✕;
 *   · las añadidas a mano y los correos de fuera, sí;
 *   · lo que todavía no ha recibido el aviso sale en ámbar, y el recuento va en «Volver a avisar».
 *
 * «Sin avisar» NO es una función nueva: sale de comparar lo que el detalle ya trae, los miembros de
 * cada área y la lista de avisados (con `persona_id` o, para un correo de fuera, `email`).
 * `estaAvisado(m)` y `correoAvisado(c)` los da quien llama, porque alta y baja traen la lista de
 * avisados con forma distinta.
 *
 * `derivadas: null` es el ALTA: allí las áreas son una sola lista guardada (`incorp_alta_areas`),
 * todas quitables, y no hay áreas «por lo que tienen que hacer». No se pinta ese grupo vacío —
 * sería decir que existe algo que en el alta no existe— y el de las elegidas se titula `tituloElegidas`.
 */
const Cuantos = ({ n }) => (
  /* El cero va en ámbar: «Sistemas · 0 pers.» se tiene que leer como aviso, porque es exactamente
     lo que hace que un aviso salga a cero sin que nada lo diga. */
  <span className={`text-[10px] ${n === 0 ? 'text-amber-700' : 'text-v25-ink-400'}`}>· {n} pers.</span>
);

function ChipArea({ area, sinAvisar, onQuitar, ocupada }) {
  const n = (area.miembros || []).length;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-v25-full border py-0.5 pl-2 ${onQuitar ? 'pr-1' : 'pr-2'} text-xs ${sinAvisar ? 'border-amber-300 bg-amber-50' : 'border-v25-line bg-white'}`}
      /* `por`: las tareas que traen esta área. Sólo llega al crear la baja, y contesta «¿por qué
         Renting?» sin abrir nada. */
      title={area.por && area.por.length ? area.por.join('\n') : undefined}>
      <span className="h-1.5 w-1.5 rounded-v25-full" style={{ background: area.color || '#64748b' }} />
      {area.nombre} <Cuantos n={n} />
      {sinAvisar > 0 && <span className="rounded-v25-full bg-amber-500 px-1.5 text-[10px] font-medium text-white">{sinAvisar} sin avisar</span>}
      {onQuitar && (
        <button type="button" onClick={onQuitar} disabled={ocupada}
          className="rounded-v25-full p-0.5 text-v25-ink-400 hover:bg-v25-expired-bg hover:text-v25-expired-fg disabled:opacity-50"
          title={`Quitar «${area.nombre}»`} aria-label={`Quitar ${area.nombre}`}>
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

export function QuienSeEntera({
  derivadas, elegidas, correos, estaAvisado, correoAvisado, tituloElegidas = 'Añadidas a mano',
  editable, ocupada, catalogo,
  areaAAvisar, setAreaAAvisar, onAnadirArea, onQuitarArea,
  correoAAvisar, setCorreoAAvisar, errorCorreo, onAnadirCorreo, onQuitarCorreo,
  sinAvisos = false,
}) {
  /* `sinAvisos`: el proceso todavía no existe (el diálogo de crear la baja). No hay avisos que
   * comparar, y pintar a todos «sin avisar» en ámbar sería marcar como pendiente lo que es normal. */
  const sinAvisarDe = (a) => (sinAvisos ? 0 : (a.miembros || []).filter((m) => !estaAvisado(m)).length);
  const titulo = (t) => <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-v25-ink-400">{t}</p>;
  const ninguno = (t) => <p className="text-sm text-v25-ink-500">{t}</p>;
  const libres = catalogo.filter((a) => !elegidas.some((x) => Number(x.id) === Number(a.id)));

  return (
    <section className="rounded-v25-lg border border-v25-line bg-white">
      <CabeceraColumna icono={UsersRound} titulo="Quién se entera" />
      <div className="space-y-4 p-4">
        {derivadas && (
          <div>
            {titulo('Por lo que tienen que hacer')}
            {derivadas.length === 0
              ? ninguno('Ninguna tarea tiene área asignada.')
              : <div className="flex flex-wrap gap-1.5">{derivadas.map((a) => <ChipArea key={a.id} area={a} sinAvisar={sinAvisarDe(a)} />)}</div>}
          </div>
        )}

        <div>
          {titulo(tituloElegidas)}
          {elegidas.length === 0
            ? ninguno('Ninguna.')
            : (
              <div className="flex flex-wrap gap-1.5">
                {elegidas.map((a) => (
                  <ChipArea key={a.id} area={a} sinAvisar={sinAvisarDe(a)} ocupada={ocupada}
                    onQuitar={editable ? () => onQuitarArea(a) : null} />
                ))}
              </div>
            )}
          {editable && (
            <div className="mt-2 flex items-center gap-2">
              <select value={areaAAvisar} onChange={(e) => setAreaAAvisar(e.target.value)}
                className="min-w-0 flex-1 rounded-v25-md border border-v25-line px-2 py-1.5 text-xs"
                aria-label="Área que se añade">
                <option value="">Añadir área…</option>
                {libres.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
              </select>
              <Button variant="outline" size="sm" onClick={onAnadirArea} disabled={!areaAAvisar || ocupada}>
                {ocupada ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}Añadir
              </Button>
            </div>
          )}
        </div>

        <div>
          {titulo('Correos de fuera')}
          {correos.length === 0
            ? ninguno('Ninguno.')
            : (
              <div className="flex flex-wrap gap-1.5">
                {correos.map((c) => {
                  const sin = !sinAvisos && !correoAvisado(c);
                  return (
                    <span key={c} className={`inline-flex items-center gap-1.5 rounded-v25-full border py-0.5 pl-2 ${editable ? 'pr-1' : 'pr-2'} text-xs ${sin ? 'border-amber-300 bg-amber-50' : 'border-v25-line bg-white'}`}>
                      <Mail className="h-3 w-3 text-v25-ink-400" />{c}
                      {sin && <span className="rounded-v25-full bg-amber-500 px-1.5 text-[10px] font-medium text-white">sin avisar</span>}
                      {editable && (
                        <button type="button" onClick={() => onQuitarCorreo(c)} disabled={ocupada}
                          className="rounded-v25-full p-0.5 text-v25-ink-400 hover:bg-v25-expired-bg hover:text-v25-expired-fg disabled:opacity-50"
                          title={`Quitar «${c}»`} aria-label={`Quitar ${c}`}>
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            )}
          {editable && (
            <div className="mt-2">
              <div className="flex items-center gap-2">
                <Input type="email" value={correoAAvisar}
                  onChange={(e) => setCorreoAAvisar(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAnadirCorreo(); } }}
                  placeholder="correo@ejemplo.com" aria-label="Correo de fuera al que avisar" className="min-w-0 flex-1" />
                <Button variant="outline" size="sm" onClick={onAnadirCorreo} disabled={!correoAAvisar.trim() || ocupada}>
                  {ocupada ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}Añadir
                </Button>
              </div>
              {errorCorreo && <p className="mt-1 text-[11px] text-v25-expired-fg">{errorCorreo}</p>}
              {/* Esta SÍ se queda: no explica una relación entre dos partes de la pantalla, avisa de
                  algo que pasa FUERA de ella. Un correo mal escrito no da error: no llega y ya. */}
              <p className="mt-1 text-[11px] text-v25-ink-400">
                Compruébalo bien: si está mal escrito no llegará, y nada avisará de que no llegó.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
